import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { LiveExportDraftDto } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { useMarkExportBatchUploaded } from '@/hooks/queries/useFinanceReports';
import {
  filingDestinationPhrase,
  usePortalUploadModel,
} from '@/hooks/finance/usePortalUploadModel';
import { formatDate } from '../utils';

interface ExportUploadGateBannerProps {
  /** The report whose detail this banner mounts on. */
  reportId: string;
  /** The campaign's single live Generated draft, or null when nothing is pending. */
  liveExportDraft: LiveExportDraftDto | null;
}

/**
 * The §4.1 upload-acceptance gate (ADR-020). Derived ENTIRELY from the live
 * `Generated` export draft on the report-detail read — no session/localStorage
 * state — so it persists across reload and the server's campaign-scoped export
 * block stays the source of truth.
 *
 * The question is deliberately about ACCEPTANCE, not the upload click: the AL FCPA
 * portal is the only validator (there is no offline AL validator), so it can reject
 * a malformed file. Answering "Yes" to a rejected upload would flip the rows to
 * "reported" and silently drop them from every future export. "Yes, it imported"
 * commits the rows; "No, discard" returns them to the not-yet-uploaded pool so the
 * treasurer can re-export.
 */
export function ExportUploadGateBanner({ reportId, liveExportDraft }: ExportUploadGateBannerProps) {
  const { markExportBatchUploaded } = useMarkExportBatchUploaded();
  // Name the campaign's OWN portal (AL → "the Alabama FCPA portal") instead of
  // hardcoding Alabama — the banner also renders for other incremental/portal
  // jurisdictions (e.g. VA COMET).
  const portalDestination = filingDestinationPhrase(usePortalUploadModel());
  const [busy, setBusy] = useState<'accept' | 'discard' | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!liveExportDraft) {
    return null;
  }

  const answer = async (accepted: boolean) => {
    setBusy(accepted ? 'accept' : 'discard');
    setError(null);
    try {
      await markExportBatchUploaded({
        exportBatchId: liveExportDraft.exportBatchId,
        accepted,
        reportId,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to confirm the export upload');
    } finally {
      setBusy(null);
    }
  };

  const rowCount = liveExportDraft.rowCount;
  const rowLabel = rowCount === 1 ? 'transaction' : 'transactions';

  return (
    <div
      className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-3"
      data-testid="export-upload-gate-banner"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div className="space-y-1">
          <p className="text-sm font-semibold text-amber-900">
            An export is waiting to be confirmed
          </p>
          <p className="text-sm text-amber-800">
            You downloaded an export of {rowCount} {rowLabel} on{' '}
            {formatDate(liveExportDraft.generatedAt.slice(0, 10))}. Did {portalDestination} accept the
            upload? Confirm only after the portal shows it imported — answering yes to a file the
            portal rejected would hide those transactions from every future export.
          </p>
          {/* Make the append-only doubling risk explicit (ADR-020) — mirrors the
              Amend Report modal's "append-only / double the period" wording. */}
          <p className="text-sm text-amber-800">
            Re-uploading an accepted file to the portal doubles every row — the export only ever
            pulls new transactions.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          onClick={() => answer(true)}
          disabled={busy !== null}
          data-testid="export-gate-accept"
        >
          {busy === 'accept' ? 'Confirming…' : 'Yes, it imported (irreversible)'}
        </Button>
        <Button
          variant="outline"
          onClick={() => answer(false)}
          disabled={busy !== null}
          data-testid="export-gate-discard"
        >
          {busy === 'discard' ? 'Discarding…' : 'No, discard'}
        </Button>
      </div>
      {error && (
        <p className="text-sm text-destructive" data-testid="export-gate-error">
          {error}
        </p>
      )}
    </div>
  );
}
